import React, { useState, useRef, useEffect } from 'react';

import Button from '../../../components/ui/Button';

const VideoPlayer = ({ 
  videoUrl, 
  title, 
  duration, 
  currentTime, 
  onTimeUpdate, 
  onEnded,
  isPlaying,
  onPlayPause 
}) => {
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [isPipActive, setIsPipActive] = useState(false);

  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 2];

  // Placeholder used only when an org hasn't set a real video URL yet, so testing/demo
  // playback still works and it's visually obvious this isn't real course content.
  const PLACEHOLDER_VIDEO_URL = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';
  const resolvedVideoUrl = videoUrl || PLACEHOLDER_VIDEO_URL;
  const isPlaceholder = !videoUrl;

  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (!isDragging) {
        onTimeUpdate(video?.currentTime);
      }
    };

    const handleEnded = () => {
      onEnded();
    };

    const handleLoadedMetadata = () => {
      video.currentTime = currentTime;
    };

    video?.addEventListener('timeupdate', handleTimeUpdate);
    video?.addEventListener('ended', handleEnded);
    video?.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      video?.removeEventListener('timeupdate', handleTimeUpdate);
      video?.removeEventListener('ended', handleEnded);
      video?.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [currentTime, onTimeUpdate, onEnded, isDragging]);

  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    if (isPlaying) {
      video?.play();
    } else {
      video?.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    const video = videoRef?.current;
    if (video) {
      video.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  useEffect(() => {
    const video = videoRef?.current;
    if (video) {
      video.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds?.toString()?.padStart(2, '0')}`;
  };

  const handleProgressClick = (e) => {
    const video = videoRef?.current;
    const progressBar = progressRef?.current;
    if (!video || !progressBar) return;

    const rect = progressBar?.getBoundingClientRect();
    const clickX = e?.clientX - rect?.left;
    const newTime = (clickX / rect?.width) * duration;
    
    video.currentTime = newTime;
    onTimeUpdate(newTime);
  };

  const handleProgressDrag = (e) => {
    if (!isDragging) return;
    
    const video = videoRef?.current;
    const progressBar = progressRef?.current;
    if (!video || !progressBar) return;

    const rect = progressBar?.getBoundingClientRect();
    const dragX = Math.max(0, Math.min(e?.clientX - rect?.left, rect?.width));
    const newTime = (dragX / rect?.width) * duration;
    
    video.currentTime = newTime;
    onTimeUpdate(newTime);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleProgressClick(e);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    handleProgressDrag(e);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  const toggleFullscreen = () => {
    const container = videoRef?.current?.parentElement;
    if (!container) return;

    if (!document.fullscreenElement) {
      container?.requestFullscreen()?.then(() => {
        setIsFullscreen(true);
      });
    } else {
      document.exitFullscreen()?.then(() => {
        setIsFullscreen(false);
      });
    }
  };

  const togglePictureInPicture = async () => {
    const video = videoRef?.current;
    if (!video) return;

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (document.pictureInPictureEnabled) {
        await video.requestPictureInPicture();
      }
    } catch (err) {
      // Silently ignore — PiP isn't supported in every browser (e.g. some mobile Safari cases)
    }
  };

  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    const handleEnterPip = () => setIsPipActive(true);
    const handleLeavePip = () => setIsPipActive(false);

    video?.addEventListener('enterpictureinpicture', handleEnterPip);
    video?.addEventListener('leavepictureinpicture', handleLeavePip);

    return () => {
      video?.removeEventListener('enterpictureinpicture', handleEnterPip);
      video?.removeEventListener('leavepictureinpicture', handleLeavePip);
    };
  }, []);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e?.target?.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="relative bg-black rounded-lg overflow-hidden group">
      <video
        ref={videoRef}
        src={resolvedVideoUrl}
        className="w-full aspect-video object-contain bg-black"
        onClick={onPlayPause}
        onMouseMove={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
        playsInline
      />

      {isPlaceholder && (
        <div className="absolute top-3 left-3 bg-warning/90 text-white text-xs font-semibold px-2.5 py-1 rounded-full pointer-events-none">
          Sample video — no link set yet
        </div>
      )}
      {/* Video Controls Overlay */}
      <div 
        className={`
          absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent
          transition-opacity duration-300
          ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0'}
        `}
        onMouseMove={() => setShowControls(true)}
      >
        {/* Center Play Button */}
        {!isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Button
              variant="ghost"
              size="icon"
              className="w-16 h-16 bg-white/20 hover:bg-white/30 rounded-full backdrop-blur-sm"
              onClick={onPlayPause}
              iconName="Play"
              iconSize={32}
            />
          </div>
        )}

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-4 space-y-3">
          {/* Progress Bar */}
          <div className="relative">
            <div
              ref={progressRef}
              className="w-full h-2 bg-white/30 rounded-full cursor-pointer"
              onClick={handleProgressClick}
              onMouseDown={handleMouseDown}
            >
              <div
                className="h-full bg-accent rounded-full relative"
                style={{ width: `${progressPercentage}%` }}
              >
                <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-accent rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </div>

          {/* Control Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Play/Pause */}
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={onPlayPause}
                iconName={isPlaying ? "Pause" : "Play"}
                iconSize={20}
              />

              {/* Volume */}
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={toggleMute}
                  iconName={isMuted || volume === 0 ? "VolumeX" : volume < 0.5 ? "Volume1" : "Volume2"}
                  iconSize={18}
                />
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-16 h-1 bg-white/30 rounded-full appearance-none slider"
                />
              </div>

              {/* Time Display */}
              <span className="text-white text-sm font-medium">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            <div className="flex items-center space-x-3">
              {/* Speed Control */}
              <div className="relative group/speed">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 text-xs"
                >
                  {playbackSpeed}x
                </Button>
                <div className="absolute bottom-full right-0 mb-2 bg-black/90 rounded-lg p-2 opacity-0 group-hover/speed:opacity-100 transition-opacity pointer-events-none group-hover/speed:pointer-events-auto">
                  <div className="flex flex-col space-y-1">
                    {speedOptions?.map((speed) => (
                      <button
                        key={speed}
                        onClick={() => setPlaybackSpeed(speed)}
                        className={`
                          px-3 py-1 text-xs rounded transition-colors
                          ${playbackSpeed === speed 
                            ? 'bg-accent text-white' :'text-white hover:bg-white/20'
                          }
                        `}
                      >
                        {speed}x
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Picture in Picture */}
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={togglePictureInPicture}
                iconName={isPipActive ? "PictureInPicture2" : "PictureInPicture"}
                iconSize={18}
              />

              {/* Fullscreen */}
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={toggleFullscreen}
                iconName={isFullscreen ? "Minimize" : "Maximize"}
                iconSize={18}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;